import { DatePipe, AsyncPipe } from '@angular/common';
import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CompanyProductPhaseBoard } from 'src/app/models/company_product_phase_board.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatLabel, MatFormField, MatSuffix, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatChipGrid, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatAutocompleteTrigger, MatAutocomplete, MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import dayjs from 'dayjs';
import { CompanyProductUserMember } from 'src/app/models/company_product_user_member.model';

@Component({
  selector: 'app-edit-company-product-phase-board-dialog',
  templateUrl: './edit-company-product-phase-board-dialog.component.html',
  styleUrls: ['./edit-company-product-phase-board-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatLabel,
    MatFormField,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatHint,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatIcon,
    MatChipInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatDialogActions,
    MatButton,
    AsyncPipe,
    DatePipe,
  ],
})
export class EditCompanyProductPhaseBoardDialogComponent implements OnInit {
  private readonly api = inject(ApiService);

  private readonly data = inject<{
    board: CompanyProductPhaseBoard;
    company_product_id: string;
    company_id: number;
  }>(MAT_DIALOG_DATA);

  private readonly dialog_ref = inject(MatDialogRef<EditCompanyProductPhaseBoardDialogComponent>);
  private readonly fb = inject(UntypedFormBuilder);

  form: UntypedFormGroup = this.fb.group({
    title: ['', Validators.compose([Validators.required])],
    start_date: [''],
    end_date: [''],
    default_followers: [[]],
  });

  board: CompanyProductPhaseBoard = this.data.board;

  startDateControl: UntypedFormControl = new UntypedFormControl(new Date(), Validators.required);
  startTimeControl: UntypedFormControl = new UntypedFormControl(
    '',
    Validators.compose([Validators.required, Validators.pattern(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/)])
  );

  endDateControl: UntypedFormControl = new UntypedFormControl(new Date(new Date().setDate(new Date().getDate() + 14)), Validators.required);
  endTimeControl: UntypedFormControl = new UntypedFormControl(
    '',
    Validators.compose([Validators.required, Validators.pattern(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/)])
  );

  company_product_phase_users: User[] = [];
  usersSearchCtrl = new FormControl('');
  filteredUsers: Observable<User[]>;

  company_product_id: string = this.data.company_product_id;

  constructor() {
    this.initFilteredUsers();
  }

  ngOnInit(): void {
    this.patchBoard();
    this.listenDateTimeControl();
    this.listenStartTimeControl();
    this.fetchCompanyProductPhaseUsers();
  }

  private patchBoard() {
    if (this.board.id) {
      this.form.patchValue({
        title: this.board.title,
      });
      if (this.board.start_date) {
        this.startDateControl.setValue(new Date(this.board.start_date));
        (this.startTimeControl.setValue(dayjs(this.board.start_date).format('HH:mm')),
          this.form.patchValue({
            start_date: new Date(this.board.start_date),
          }));
      } else {
        this.startDateControl.setValue(new Date());
        this.form.patchValue({
          start_date: new Date(),
        });
      }
      if (this.board.end_date) {
        this.endDateControl.setValue(new Date(this.board.end_date));
        (this.endTimeControl.setValue(dayjs(this.board.end_date).format('HH:mm')),
          this.form.patchValue({
            end_date: new Date(this.board.end_date),
          }));
      } else {
        this.endDateControl.setValue(new Date(new Date().setDate(new Date().getDate() + 14)));
        this.form.patchValue({
          end_date: new Date(new Date().setDate(new Date().getDate() + 14)),
        });
      }

      if (this.board.default_followers && this.board.default_followers.length) {
        this.form.patchValue({
          default_followers: this.board.default_followers,
        });
      }
    } else {
      const start_date = this.board.start_date ? new Date(this.board.start_date) : new Date();
      const end_date = this.board.end_date
        ? new Date(this.board.end_date)
        : new Date(new Date(start_date).setDate(start_date.getDate() + 14));

      this.startDateControl.setValue(start_date);
      this.startTimeControl.setValue(dayjs(start_date).format('HH:mm'));
      this.endDateControl.setValue(end_date);
      this.endTimeControl.setValue(dayjs(end_date).format('HH:mm'));
      this.form.patchValue({
        start_date,
        end_date,
      });
    }
  }

  private listenDateTimeControl() {
    this.startDateControl.valueChanges.subscribe((data: Date) => {
      if (this.startDateControl.valid) {
        const res_hours_minutes = this.startTimeControl.value.split(':');
        const hours = res_hours_minutes[0];
        const minutes = res_hours_minutes[1];
        const new_date: Date = new Date(data);
        new_date.setHours(hours);
        new_date.setMinutes(minutes);
        this.form.patchValue({
          start_date: new_date,
        });
      } else {
        this.form.patchValue({
          start_date: new Date(),
        });
      }
    });

    this.endDateControl.valueChanges.subscribe((data: Date) => {
      if (this.endDateControl.valid) {
        const res_hours_minutes = this.endTimeControl.value.split(':');
        const hours = res_hours_minutes[0];
        const minutes = res_hours_minutes[1];
        const new_date: Date = new Date(data);
        new_date.setHours(hours);
        new_date.setMinutes(minutes);
        this.form.patchValue({
          end_date: new_date,
        });
      } else {
        this.form.patchValue({
          end_date: new Date(new Date().setDate(new Date().getDate() + 14)),
        });
      }
    });
  }

  private listenStartTimeControl() {
    this.startTimeControl.valueChanges.subscribe(data => {
      if (this.startTimeControl.valid) {
        const splitted: number[] = data.split(':');
        const current_date: Date = new Date(this.startDateControl.value);
        current_date.setHours(splitted[0]);
        current_date.setMinutes(splitted[1]);
        this.form.patchValue({
          start_date: current_date,
        });
      }
    });
    this.endTimeControl.valueChanges.subscribe(data => {
      if (this.endTimeControl.valid) {
        const splitted: number[] = data.split(':');
        const current_date: Date = new Date(this.endDateControl.value);
        current_date.setHours(splitted[0]);
        current_date.setMinutes(splitted[1]);
        this.form.patchValue({
          end_date: current_date,
        });
      }
    });
  }

  save() {
    const data = { ...this.form.value };
    data.default_followers = data.default_followers.map((u: User) => u.id);

    if (this.board.id) {
      this.api.updateCompanyProductPhaseBoard((this.board.id as number).toString(), data).subscribe(() => {
        this.dialog_ref.close(this.form.value);
      });

      return;
    }

    data.company_product_phase_id = this.board.company_product_phase_id;
    this.api.createCompanyProductPhaseBoard(data).subscribe(response => {
      this.dialog_ref.close(response);
    });
  }

  onNoClick(): void {
    this.dialog_ref.close();
  }

  onCancel() {
    this.dialog_ref.close();
  }

  removeUser(idx: number) {
    const users: User[] = this.form.value.default_followers;
    users.splice(idx, 1);
    this.form.controls['default_followers'].patchValue(users);
  }

  selectedUser(user: User) {
    const users: User[] = this.form.value.default_followers;
    users.push(user);
    this.form.controls['default_followers'].patchValue(users);
    this.usersSearchCtrl.setValue('');
  }

  private initFilteredUsers() {
    this.filteredUsers = this.usersSearchCtrl.valueChanges.pipe(
      startWith(null),
      map((user: string | null) => (user && typeof user === 'string' ? this._filterUsers(user) : this.company_product_phase_users.slice()))
    );
  }

  private _filterUsers(user: string): User[] {
    const filterValue = user.toLowerCase();
    return this.company_product_phase_users.filter(user => user.name.toLowerCase().includes(filterValue));
  }

  private fetchCompanyProductPhaseUsers() {
    this.api.getActiveCompanyProductMembersWithBusiness(this.company_product_id).subscribe(users => {
      this.company_product_phase_users = users.bisual_users.map((obj: CompanyProductUserMember): User => obj.user);
      users.company_users.forEach(cu => this.company_product_phase_users.push(cu));
    });
  }
}
